import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, showAlert, Tabs } from '@silverfort/sf-components';
import { useIspmContext } from '../IspmContext';
import { getRiskIndicatorByRiskRule, RISK_RULES } from '@silverfort-nodelibs/common/utils/insight';
import InsightOverviewCard from './InsightOverview/InsightOverviewCard';
import InsightMitigation from './InsightMitigation/InsightMitigation';
import InsightGraphCard from './InsightOverview/InsightGraphCard/InsightGraphCard';
import EntitiesTable, { SnackbarDataProps } from '../EntitiesTable/EntitiesTable';
import EntitiesDecisionSnackbar from '../EntitiesTable/EntitiesDecisionSnackbar';
import { AZURE_ENVIRONMENT, IspmTab } from '../constants';
import { HTTP_RES_STATUS_CODES } from '@silverfort-nodelibs/common/utils/consts';
import CustomExposureConfigurationContent from '../ExposuresConfiguration/components/CustomExposureConfigurationContent';
import {
    ExposureConfig,
    SaveExposuresConfigPayload,
    StaticExposureCustomConfig
} from '../ExposuresConfiguration/types';
import useSaveExposuresConfiguration from '../api/mutations/useSaveExposuresConfiguration';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import useDeleteCustomExposure from '../api/mutations/useDeleteCustomExposures';
import { CustomExposureConfig } from '../ExposuresConfiguration/types';
import StaticExposureConfigurationContent from '../ExposuresConfiguration/components/StaticExposureConfigurationContent';

const STALE_DATA_ERROR_MESSAGE = 'Data is stale - refreshing page with latest insights';

const SUCCESS_MESSAGE = 'Configuration saved successfully';
const ERROR_MESSAGE = 'Failed to save configuration';

interface SpecificInsightDrawerProps {
    exposuresConfigurations: ExposureConfig[];
    refetchSettings: () => void;
}

const SpecificInsightDrawer: React.FC<SpecificInsightDrawerProps> = ({ exposuresConfigurations, refetchSettings }) => {
    const {
        customInsightsIdsToNames,
        domainFilter,
        selectedInsight,
        selectedLastChangesFilter,
        setSelectedInsight,
        insightsData,
        refreshInsightsData,
        hasIspmEditorPermission
    } = useIspmContext();
    const riskIndicator = getRiskIndicatorByRiskRule(selectedInsight);
    const [snackbarData, setSnackbarData] = useState<SnackbarDataProps>();
    const [activeTab, setActiveTab] = useState<IspmTab>(IspmTab.OVERVIEW);
    const [overviewTabSupported, setOverviewTabSupported] = useState<boolean>(true);

    useEffect(() => {
        if (
            selectedInsight &&
            insightsData?.hiddenRiskIndicators &&
            insightsData.hiddenRiskIndicators.includes(selectedInsight)
        ) {
            setActiveTab(IspmTab.CUSTOMIZATION);
            setOverviewTabSupported(false);
        } else {
            setOverviewTabSupported(true);
            if (!selectedInsight) {
                setActiveTab(IspmTab.OVERVIEW);
            }
        }
    }, [insightsData, selectedInsight]);

    const handleTabChange = (tab: IspmTab) => {
        if (tab === IspmTab.OVERVIEW && !overviewTabSupported) {
            return;
        }
        if (tab === IspmTab.CUSTOMIZATION && !hasIspmEditorPermission) {
            return;
        }
        setActiveTab(tab);
    };
    const onDeleteSuccess = useCallback(() => {
        showAlert('Configuration deleted successfully', 'success');
        refetchSettings();
        refreshInsightsData();
        setSelectedInsight(null);
    }, [refetchSettings, refreshInsightsData, setSelectedInsight]);

    const onDeleteError = useCallback(() => {
        showAlert('Failed to delete configuration', 'error');
    }, []);

    const { deleteCustomExposures, loading: isDeleting } = useDeleteCustomExposure({
        onSuccess: onDeleteSuccess,
        onError: onDeleteError
    });

    const onSuccess = useCallback(() => {
        showAlert(SUCCESS_MESSAGE, 'success');
        refetchSettings();
    }, [refetchSettings]);

    const onError = useCallback(() => {
        showAlert(ERROR_MESSAGE, 'error');
    }, []);

    const { saveExposuresConfigurations, loading: isSaving } = useSaveExposuresConfiguration({ onSuccess, onError });

    const insightName = useMemo(() => {
        const selectedInsightKey = selectedInsight ?? '';
        const riskIndicator = getRiskIndicatorByRiskRule(selectedInsight);
        const name =
            riskIndicator?.insightsTitle ?? customInsightsIdsToNames?.[selectedInsightKey] ?? selectedInsightKey;
        return typeof name === 'string' ? name : selectedInsightKey;
    }, [customInsightsIdsToNames, selectedInsight]);

    // Map from rule ID to indicator ID
    const getIndicatorIdFromRuleId = (ruleId: string): string => {
        const ruleEntry = Object.entries(RISK_RULES).find(([, value]) => value === ruleId);
        return ruleEntry ? ruleEntry[0] : ruleId;
    };

    const config = useMemo((): StaticExposureCustomConfig | CustomExposureConfig | null => {
        if (!exposuresConfigurations || !selectedInsight) return null;

        const indicatorId = getIndicatorIdFromRuleId(selectedInsight);
        const existing = exposuresConfigurations.find((i) => i.id === indicatorId) as
            | StaticExposureCustomConfig
            | CustomExposureConfig;

        const baseConfig = {
            id: indicatorId,
            name: indicatorId,
            displayName: insightName,
            description: riskIndicator?.description || existing?.description || '',
            category: riskIndicator?.category?.title ?? existing?.category ?? 'N/A',
            isActive: existing?.isActive !== false,
            excludedUsersAndGroups: existing?.excludedUsersAndGroups || [],
            excludedConditions: existing?.excludedConditions || [],
            usersAndGroups: existing?.usersAndGroups || [],
            conditions: existing?.conditions ?? [],
            customizableFields: existing?.customizableFields || {},
            isStatic: existing?.isStatic !== false,
            entityType: existing?.entityType || AD_ENTITY_TYPES.USER,
            adEntityType: existing?.adEntityType || AD_ENTITY_TYPES.USER
        };

        // Return proper type based on whether it's a custom rule
        if (!baseConfig.isStatic) {
            return baseConfig as CustomExposureConfig;
        } else {
            return baseConfig as StaticExposureCustomConfig;
        }
    }, [exposuresConfigurations, selectedInsight, insightName, riskIndicator]);

    const onUpdateEntityStatusError = (newStatusDescription: string, err?: any) => {
        if (err?.status === HTTP_RES_STATUS_CODES.CONFLICT) {
            const staleDataMessage = err?.response?.data?.message || STALE_DATA_ERROR_MESSAGE;
            showAlert(staleDataMessage, 'error');
            return;
        }
        showAlert(`Failed to update status to ${newStatusDescription}`, 'error');
    };

    const refresh = useCallback(() => {
        refetchSettings();
        refreshInsightsData();
    }, [refetchSettings, refreshInsightsData]);

    const saveExposureConfiguration = useCallback(
        async (config: SaveExposuresConfigPayload) => {
            await saveExposuresConfigurations(config);
            refresh();
        },
        [saveExposuresConfigurations, refresh]
    );

    const drawerMode = useMemo(() => {
        if (!config) return null;
        return !config.isStatic ? 'edit-custom' : 'static';
    }, [config]);

    const disabledTabs = useMemo(() => {
        const tabs: IspmTab[] = [];
        if (!overviewTabSupported) {
            tabs.push(IspmTab.OVERVIEW);
        }
        if (!hasIspmEditorPermission) {
            tabs.push(IspmTab.CUSTOMIZATION);
        }
        return tabs;
    }, [overviewTabSupported, hasIspmEditorPermission]);

    return (
        <Drawer
            anchor="right"
            className="max-w-[70vw]"
            contentClassName="!min-w-[70vw] max-w-[70vw] p-[0px] divide-y divide-gray-100"
            footerActionTitle={activeTab === IspmTab.OVERVIEW ? 'Close' : undefined}
            headerDescription={typeof riskIndicator?.description === 'string' ? riskIndicator.description : ''}
            headerTitle={typeof insightName === 'string' ? insightName : 'Insight'}
            onAction={
                activeTab === IspmTab.OVERVIEW
                    ? () => {
                          setSelectedInsight(null);
                      }
                    : undefined
            }
            onClose={() => {
                setSelectedInsight(null);
                handleTabChange(IspmTab.OVERVIEW);
            }}
            open={!!selectedInsight}>
            <div className="flex overflow-hidden flex-col w-full h-full divide-y divide-gray-200">
                {config?.category && (
                    <div className="flex flex-col gap-2 items-start px-6 py-4">
                        <span className="text-gray-600 text-13">Categories</span>
                        <span className="p-1 w-auto font-medium text-gray-800 rounded-sm border border-gray-100 text-12">
                            {config.category}
                        </span>
                    </div>
                )}
                <Tabs
                    className="px-6"
                    selectedTab={activeTab}
                    onClick={handleTabChange}
                    tabs={[
                        { value: IspmTab.OVERVIEW, name: IspmTab.OVERVIEW },
                        { value: IspmTab.CUSTOMIZATION, name: IspmTab.CUSTOMIZATION }
                    ]}
                    disabledTabs={disabledTabs}
                />

                {/* Tab Content */}
                <div className="overflow-y-auto flex-1 scrollbar-hide">
                    {activeTab === IspmTab.OVERVIEW && (
                        <div className="flex flex-col gap-6 p-6 w-full h-full">
                            <div className="flex flex-none justify-between w-full h-[190px]">
                                <div className="h-full w-[320px]">
                                    <InsightOverviewCard />
                                </div>
                                <div className="h-full w-[calc(100%-320px-25px)]">
                                    <InsightGraphCard />
                                </div>
                            </div>
                            <div className="flex-none w-full">
                                <InsightMitigation
                                    mitigationDescription={
                                        typeof riskIndicator?.mitigation === 'string'
                                            ? riskIndicator.mitigation
                                            : 'No mitigation description found...'
                                    }
                                    reference={
                                        typeof riskIndicator?.reference === 'string'
                                            ? riskIndicator.reference
                                            : undefined
                                    }
                                />
                            </div>
                            <div className="overflow-x-auto w-full h-full">
                                <EntitiesTable
                                    setSnackbarData={setSnackbarData}
                                    insightRuleName={selectedInsight ?? ''}
                                    diffInDaysFilter={+selectedLastChangesFilter}
                                    domainNameFilter={domainFilter ?? undefined}
                                    sourceTypeFilter={AZURE_ENVIRONMENT}
                                    hiddenRiskIndicators={insightsData?.hiddenRiskIndicators}
                                    onEntityStatusUpdate={refresh}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === IspmTab.CUSTOMIZATION && config && (
                        <div className="w-full h-full">
                            {drawerMode === 'static' && (
                                <StaticExposureConfigurationContent
                                    config={config as StaticExposureCustomConfig}
                                    onSave={saveExposureConfiguration}
                                    isSaving={isSaving}
                                />
                            )}
                            {drawerMode === 'edit-custom' && (
                                <CustomExposureConfigurationContent
                                    config={config as CustomExposureConfig}
                                    onSave={saveExposureConfiguration}
                                    onDelete={deleteCustomExposures}
                                    isSaving={isSaving || isDeleting}
                                    isNew={false}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Snackbar */}
                {snackbarData?.selectedEntities && snackbarData.selectedEntities.length > 0 && (
                    <EntitiesDecisionSnackbar
                        isOpen={true}
                        onClose={snackbarData.onClose}
                        selectedEntities={snackbarData?.selectedEntities}
                        allEntities={snackbarData?.allEntities}
                        onSuccess={snackbarData.onSuccess}
                        onError={onUpdateEntityStatusError}
                    />
                )}
            </div>
        </Drawer>
    );
};

export default SpecificInsightDrawer;
